import React, {useState} from "react";
import {IconButton, Menu, MenuItem} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ProductDeleteDialogBox from "./ProductDeleteDialogBox";
import ProductsFormModal from "./ProductsFormModal";
import {useDeleteProductHandler} from "../dataHandlers/deleteProductHandler";
import {DEFAULT_TAX_SCHEME} from "../../../constants/defaults";
import PermissionDeniedWrapper from "../../../components/PermissionDeniedWrapper";
import {usePermissions} from "../../../hooks/usePermissions";
import {useAccessDeniedMessage} from "../../../hooks/useAccessDeniedMessage";
import {ARCHIVE_PRODUCT, UPDATE_PRODUCT} from "../../../constants/eInvoicePermissions";

const buildClassifiedTaxCategory = (product = {}) => ({
    id: product?.classified_tax_category?.id ?? null,
    percent: product?.classified_tax_category?.percent ?? 0,
    tax_exemption_reason_code:
        product?.classified_tax_category?.tax_exemption_reason_code ?? null,
    tax_exemption_reason:
        product?.classified_tax_category?.tax_exemption_reason ?? null,
    zero_rated_reason_code:
        product?.classified_tax_category?.zero_rated_reason_code ?? null,
    zero_rated_reason:
        product?.classified_tax_category?.zero_rated_reason ?? null,
    tax_scheme:
        product?.classified_tax_category?.tax_scheme ?? DEFAULT_TAX_SCHEME,
});

const ProductActions = ({product}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [openProductForm, setOpenProductForm] = useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState(null);
    const [readOnly, setReadOnly] = useState(false);

    const {hasEInvoicePermission} = usePermissions();
    const accessDeniedMessage = useAccessDeniedMessage();
    const canUpdateProduct = hasEInvoicePermission(UPDATE_PRODUCT);
    const canArchiveProduct = hasEInvoicePermission(ARCHIVE_PRODUCT);

    const {onDelete} = useDeleteProductHandler();

    const getTransformedProduct = (productRow) => ({
        ...productRow,
        unit_price: productRow.unit_price || "",
        quantity: productRow.quantity || "",
        unit_of_measure: productRow.unit_of_measure,
        classified_tax_category: buildClassifiedTaxCategory(productRow),
        additional_item_identification:
            productRow.additional_item_identification || {
                nature_code: "",
            },
        item_specification_document_reference:
            productRow.item_specification_document_reference || {
                commodity_code: "",
                item_type: "G",
            },
        commodity_classification: productRow.commodity_classification || {
            item_classification_code: "",
            item_classification_list_id: "HS",
            item_classification_list_version_id: "1.0",
        },
    });

    const handleClick = (event) => setAnchorEl(event.currentTarget);

    const handleClose = () => {
        setReadOnly(false);
        setAnchorEl(null);
    };

    const handleDetails = () => {
        setAnchorEl(null);
        setReadOnly(true);
        setCurrentItem(getTransformedProduct(product));
        setOpenProductForm(true);
    };

    const handleEdit = () => {
        if (!canUpdateProduct) {
            return;
        }
        setAnchorEl(null);
        setReadOnly(false);
        setCurrentItem(getTransformedProduct(product));
        setOpenProductForm(true);
    };

    const handleDeleteClick = () => {
        if (!canArchiveProduct) {
            return;
        }
        setAnchorEl(null);
        setCancelDialogOpen(true);
    };

    const handleDelete = async () => {
        setCancelDialogOpen(false);
        setOpenProductForm(false);
        await onDelete(product);
        handleClose();
    };

    return (
        <>
            <IconButton
                data-testid="action-icon-button"
                onClick={handleClick}
                aria-label="more"
            >
                <MoreVertIcon/>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        style: {
                            boxShadow: "4px 4px 10px 0px rgba(0,0,0,0.2)",
                        },
                    },
                }}
            >
                <MenuItem data-testid="details" onClick={handleDetails}>
                    Details
                </MenuItem>
                <PermissionDeniedWrapper show={!canUpdateProduct} title={accessDeniedMessage}>
                    <MenuItem data-testid="edit" disabled={!canUpdateProduct} onClick={handleEdit}>
                        Edit
                    </MenuItem>
                </PermissionDeniedWrapper>
                {canArchiveProduct &&
                    <MenuItem data-testid="delete" disabled={!canArchiveProduct} onClick={handleDeleteClick}>
                        Delete
                    </MenuItem>}
            </Menu>

            <ProductsFormModal
                open={openProductForm}
                handleClose={() => setOpenProductForm(false)}
                initialData={currentItem}
                readOnly={readOnly}
            />

            <ProductDeleteDialogBox
                open={cancelDialogOpen}
                onClose={() => setCancelDialogOpen(false)}
                onSubmit={handleDelete}
                product={product}
            />
        </>
    );
};

export default ProductActions;
